import { FC, forwardRef, useEffect, useRef, useState } from 'react';

import { Box, Checkbox, DropdownItem, Label } from '@base-app';
import {
  CautionIconStyled,
  DropdownIconRotatedStyled,
  DropdownIconStyled,
  ErrorBoxStyled,
  ErrorDescriptionStyled,
  IconContainerStyled,
  SelectButtonStyled,
  SelectContainerStyled,
  SelectDropdownStyled,
  SelectInputContainerStyled,
  SelectListItemLabelInputStyled,
  SelectListItemLabelStyled,
  SelectListItemStyled,
  StyledMultiSelectListItem,
  StyledMultiSelectList,
  FilterIconStyled,
  StyledPlaceholder,
  StyledSecondaryLabel,
} from './SelectA11y.styles';
import { SelectA11yProps } from './SelectA11y.types';
import { useOnClickOutside } from '@common/hooks';
import { useAccessibleDropdown, useMenuPosition } from '@components/hooks';
import { Variants } from '@common/types';

const SelectA11y: FC<SelectA11yProps> = forwardRef<HTMLButtonElement, SelectA11yProps>(
  (props: SelectA11yProps, ref) => {
    const {
      css,
      disabled,
      testId,
      defaultValue,
      error,
      errorDescription,
      id,
      items,
      label,
      listWidth,
      multipleSelector,
      namespace = 'default_select_namespace',
      placeholder,
      secondaryLabel,
      value: val,
      onChange,
      onChangeDropdownItem,
      onSelectedOption,
      variant,
      width,
      isSensitive = false,
    } = props;

    const [value, setValue] = useState<string>(defaultValue);

    const [selectedOptions, setSelectedOptions] = useState<DropdownItem[]>([]);

    const containerRef = useRef(null);

    const chosen = items.find((item) => item.id === value);

    const adjustedVariant = variant === Variants.TERTIARY ? Variants.PRIMARY : variant;

    const handleOptionClick = (option: DropdownItem): void => {
      if (selectedOptions.find((item) => item.id === option.id)) {
        setSelectedOptions(selectedOptions.filter((item) => item.id !== option.id));
      } else {
        setSelectedOptions([...selectedOptions, option]);
      }
    };

    useEffect(() => {
      onSelectedOption?.(selectedOptions);
    }, [selectedOptions]);

    const handleChange = (item: DropdownItem): void => {
      setValue(item.id);
      onChange?.(item.id);
      onChangeDropdownItem?.(item);
      setIsDropdownOpen(false);
    };

    const { isDropdownOpen, setIsDropdownOpen, activeIndex, setActiveIndex, setIsFocus, listRef } =
      useAccessibleDropdown({ items, value, onChange: handleChange });

    const handleMouseOver = (index: number): void => {
      setActiveIndex(index);
    };

    const handleListVisibility = (): void => {
      if (disabled) {
        return;
      }

      setIsDropdownOpen(!isDropdownOpen);
    };

    const handleClickOutsideMenu = (): void => {
      setIsDropdownOpen(false);
    };

    useMenuPosition({
      isDropdownOpen,
      listRef,
      dropdownRef: containerRef,
    });

    useOnClickOutside(containerRef, handleClickOutsideMenu);

    useEffect(() => {
      setValue(val);
    }, [val]);

    return (
      <>
        <SelectContainerStyled
          css={{ ...css, width }}
          variant={adjustedVariant}
          className="select-container"
          ref={containerRef}
          disabled={disabled}
        >
          <SelectInputContainerStyled>
            <Label htmlFor={id}>{label}</Label>
            {secondaryLabel && variant === Variants.PRIMARY && (
              <StyledSecondaryLabel htmlFor={id}>
                <em>{secondaryLabel}</em>
              </StyledSecondaryLabel>
            )}
            <SelectButtonStyled
              data-testid={testId}
              ref={ref}
              type="button"
              className="select-button"
              onClick={handleListVisibility}
              onFocus={() => setIsFocus(true)}
              onBlur={() => setIsFocus(false)}
              role="combobox"
              aria-haspopup="listbox"
              aria-controls={`${namespace}_dropdown`}
              aria-expanded={isDropdownOpen}
              aria-activedescendant={`${namespace}_element_${value}`}
              variant={adjustedVariant}
              aria-disabled={disabled}
              disabled={disabled}
            >
              {chosen == null && (
                <StyledPlaceholder className="select-placeholder" variant={adjustedVariant}>
                  {placeholder}
                </StyledPlaceholder>
              )}
              {chosen != null && <Box className={isSensitive ? 'rb-mask' : ''}>{chosen?.label}</Box>}
              <IconContainerStyled disabled={disabled} data-testid="dropdownBtn">
                {multipleSelector ? (
                  <FilterIconStyled />
                ) : (
                  <>{isDropdownOpen ? <DropdownIconRotatedStyled /> : <DropdownIconStyled />}</>
                )}
              </IconContainerStyled>
            </SelectButtonStyled>

            {multipleSelector ? (
              <StyledMultiSelectList
                css={{ width: listWidth }}
                ref={listRef}
                className="select-dropdown"
                role="listbox"
                id={`${namespace}_dropdown`}
                data-testid={`${namespace}-dropdown`}
                tabIndex={-1}
              >
                {items.map((option, index) => (
                  <StyledMultiSelectListItem
                    key={option.id}
                    id={`${namespace}_element_${option.id}`}
                    data-testid={`${namespace}_element_${option.id}`}
                    aria-selected={index === activeIndex}
                    role="option"
                  >
                    <Checkbox
                      id={`checkbox-${option.id}`}
                      name={`checkbox-${option.id}`}
                      checked={!!selectedOptions.find((item) => item.id === option.id)}
                      onChange={() => handleOptionClick(option)}
                    />
                    <SelectListItemLabelStyled multipleSelector onClick={() => handleOptionClick(option)}>
                      <Box as="span" className={isSensitive ? 'rb-mask' : ''}>
                        {option.label}
                      </Box>
                    </SelectListItemLabelStyled>
                  </StyledMultiSelectListItem>
                ))}
              </StyledMultiSelectList>
            ) : (
              <SelectDropdownStyled
                css={{
                  width: listWidth,
                }}
                ref={listRef}
                className="select-dropdown"
                role="listbox"
                id={`${namespace}_dropdown`}
                tabIndex={-1}
                variant={variant}
              >
                {items.map((option, index) => (
                  <SelectListItemStyled
                    key={option.id}
                    id={`${namespace}_element_${option.id}`}
                    data-testid={`${namespace}_element_${option.id}`}
                    aria-selected={index === activeIndex}
                    role="option"
                    onMouseOver={() => handleMouseOver(index)}
                    variant={adjustedVariant}
                    onClick={() => handleChange(option)}
                  >
                    <SelectListItemLabelStyled>
                      <SelectListItemLabelInputStyled
                        type="radio"
                        name={`${namespace}_radio`}
                        data-testid={`${namespace}_element_${option.id}_radio`}
                        value={option.id}
                        className={chosen?.id === option.id ? 'selected' : ''}
                      />
                      <Box as="span" className={isSensitive ? 'rb-mask' : ''}>
                        {option.label}
                      </Box>
                    </SelectListItemLabelStyled>
                  </SelectListItemStyled>
                ))}
              </SelectDropdownStyled>
            )}

            {error != null && error && (
              <ErrorBoxStyled>
                <CautionIconStyled />
                <ErrorDescriptionStyled role="error-message">{errorDescription}</ErrorDescriptionStyled>
              </ErrorBoxStyled>
            )}
          </SelectInputContainerStyled>
        </SelectContainerStyled>
      </>
    );
  },
);

SelectA11y.displayName = 'SelectA11y';

export default SelectA11y;
